import styled, { css } from 'styled-components';

export type SegmentedControlOption = {
  label: string;
  value: string;
};

type SegmentedControlProps = {
  options: SegmentedControlOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  // In mobile, stretch the control to fill its row and split the width evenly across options.
  mobileFullWidth?: boolean;
};

export default function SegmentedControl({
  options,
  selectedValue,
  onChange,
  disabled = false,
  mobileFullWidth = false,
}: SegmentedControlProps) {
  return (
    <Container $mobileFullWidth={mobileFullWidth}>
      {options.map((option) => {
        const isSelected = option.value === selectedValue;
        return (
          <Segment
            key={option.value}
            $selected={isSelected}
            $mobileFullWidth={mobileFullWidth}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(option.value);
            }}>
            {option.label}
          </Segment>
        );
      })}
    </Container>
  );
}

const Container = styled.div<{ $mobileFullWidth?: boolean }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  height: 40px;
  padding: 0 4px;
  gap: 2px;

  ${({ $mobileFullWidth }) =>
    $mobileFullWidth &&
    css`
      @media (max-width: 800px) {
        display: flex;
        width: 100%;
      }
    `}
`;

const Segment = styled.button<{ $selected: boolean; $mobileFullWidth?: boolean }>`
  padding: 0 16px;
  height: 32px;
  font-size: 12px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 400)};
  font-family: 'Lato', sans-serif;
  letter-spacing: 0.18px;
  border: none;
  border-radius: 3px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  background-color: ${({ $selected, theme }) => ($selected ? theme.colors.primaryBlue10 : 'transparent')};
  color: ${({ $selected, theme }) => ($selected ? theme.colors.primaryBlue : theme.colors.black)};

  ${({ $mobileFullWidth }) =>
    $mobileFullWidth &&
    css`
      @media (max-width: 800px) {
        flex: 1 0 auto;
        padding: 0 8px;
      }
    `}
`;
